import { Injectable } from '@angular/core';
import { TransferenceType } from '../../enums/transference-type';
import { Aplication } from '../../interfaces/aplication';
import { Local } from '../../interfaces/local';
import { Pool } from '../../interfaces/pool';
import { Product } from '../../interfaces/product';
import { Suggestion } from '../../interfaces/suggestion';
import { LocalService } from '../local/local.service';
import { PoolService } from '../pool/pool.service';
import { ProductService } from '../product/product.service';
import { TransferenceService } from '../transference/transference.service';

@Injectable({ providedIn: 'root' })
export class AplicationService {

  private undoStockChangesSteps = [];

  constructor(
    private transferenceService: TransferenceService,
    private productService: ProductService,
    private poolService: PoolService,
    private localService: LocalService,
  ) {
  }

  public submitAplication(aplication: Aplication) {
    return this.transferenceService.addToOnHoldTransferenceList(
      TransferenceType.APLICACAO,
      aplication.localId,
      null,
      aplication.suggestion,
      aplication.aplicationProductList,
      aplication.dateTime,
    );
  }

  public updateAplication(aplication: Aplication) {
    return this.transferenceService.updateOnHoldTransference(aplication.dateTime, {
      type: TransferenceType.APLICACAO,
      fromId: aplication.localId,
      toId: null,
      suggestion: aplication.suggestion,
      productList: aplication.aplicationProductList,
      dateTime: aplication.dateTime,
    });
  }

  public async makeAplication(
    products: Product[],
    pool: Pool,
    suggestion: Suggestion,
    local: Local,
    aplicationDateTime: string,
    analyzeDateTime: string,
  ) {
    const isUpdate = await this.transferenceService.hasOnHoldAplication(aplicationDateTime);
    const restoredStock = await this.undoStockChanges();
    if (local && restoredStock) {
      local.stock = restoredStock;
    }
    const productsToApply = products;
    this.productService.updateUseHistory(productsToApply);
    return this.createAplication({
      analyzeDateTime,
      analyzeId: pool.analyzeId,
      isCreatedByIA: Boolean(suggestion && suggestion.isCreatedByIA),
      id: suggestion && suggestion.id ? suggestion.id : null,
      suggestedProducts: !suggestion || suggestion.isCreatedByIA
        ? null
        : suggestion && suggestion.suggestedProducts,
    }, aplicationDateTime, productsToApply, local)
      .then((aplication) => isUpdate
        ? this.updateAplication(aplication)
        : this.submitAplication(aplication),
      ).then(async () => {
        const analyzedPool = await this.poolService.getAnalyzedPoolByCreateDate(analyzeDateTime);
        if (analyzedPool && analyzedPool.analyzeId) {
          this.productService.updateStandbyApplication(analyzedPool.analyzeId, productsToApply);
        }
      });
  }

  public setUndoStockChangesSteps(steps) {
    this.undoStockChangesSteps = steps.map(step => ({
      ...step,
      products: step.products.map(product => ({ ...product })),
    }));
  }

  public getUndoStockChangesSteps() {
    return this.undoStockChangesSteps;
  }

  private createAplication(
    suggestion: Suggestion,
    dateTime: string,
    productsToApply: Product[],
    local: Local,
  ): Promise<Aplication> {
    const aplication: Aplication = {
      suggestion,
      dateTime,
      localId: local ? local.id : null,
      aplicationProductList: productsToApply,
    };
    if (!local) {
      return Promise.resolve(aplication);
    }
    let stockUpdatePromise;
    if (local.isUsingAnotherStock && local.localServingStock && productsToApply.length > 0) {
      aplication.localServingStockId = local.localServingStock.id;
      stockUpdatePromise = this.localStockUpdate(productsToApply, productsToApply, aplication.localServingStockId);
    } else {
      const productsThatAreMissing = this.getMissingProduts(productsToApply, local);
      if (productsThatAreMissing.length > 0 && local.localServingStock) {
        aplication.localServingStockId = local.localServingStock.id;
        stockUpdatePromise = this.localStockUpdate(
          productsToApply, productsThatAreMissing, aplication.localServingStockId,
        );
      } else {
        stockUpdatePromise = this.localStockUpdate(productsToApply);
      }
    }
    return stockUpdatePromise.then(() => aplication);
  }

  private localStockUpdate(
    productsToApply: Product[],
    outOfStockProducts?: Product[],
    localServingStockId?: string,
  ) {
    let stockUpdatePromises = [];
    if (outOfStockProducts) {
      stockUpdatePromises = [
        this.localService.localStockUpdate(TransferenceType.SAIDA, outOfStockProducts, localServingStockId),
        this.localService.localStockUpdate(TransferenceType.ENTRADA, outOfStockProducts),
      ];
      this.undoStockChangesSteps = [
        { type: TransferenceType.ENTRADA, products: outOfStockProducts, localServingStockId },
        { type: TransferenceType.SAIDA, products: outOfStockProducts },
      ];
    }
    stockUpdatePromises.push(
      this.localService.localStockUpdate(TransferenceType.SAIDA, productsToApply),
    );
    this.undoStockChangesSteps.push({ type: TransferenceType.ENTRADA, products: productsToApply });
    return Promise.all(stockUpdatePromises);
  }

  private getMissingProduts(
    products: Product[],
    local: Local,
  ) {
    return products.reduce((missingProduts, selectedProduct) => {
      const selectedProductOnStock = local.stock.find((productFromStock) =>
        productFromStock.description.id === selectedProduct.description.id);
      if (!selectedProductOnStock) {
        missingProduts.push(selectedProduct);
      } else if (selectedProductOnStock.amount < selectedProduct.amount) {
        missingProduts.push(
          {
            amount: selectedProduct.amount - selectedProductOnStock.amount,
            description: selectedProduct.description,
            local: selectedProduct.local,
          },
        );
      }
      return missingProduts;
    }, []);
  }

  private undoStockChanges() {
    const stockUpdatesPromise = [];
    this.undoStockChangesSteps.forEach((step) => {
      stockUpdatesPromise.push(
        this.localService.localStockUpdate(step.type, step.products, step.localServingStockId),
      );
    });
    return Promise.all(stockUpdatesPromise).then((updatedStocks) => {
      this.undoStockChangesSteps = [];
      return updatedStocks[updatedStocks.length - 1];
    });
  }

}
